import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Copy, Check, Users, RefreshCw, Lock, Loader2, LogOut, Zap, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "s1ep_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  if (!token) {
    return <LoginGate onAuthed={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }
  return <LeadsDashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />;
}

function LoginGate({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onAuthed(res.data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="pointer-events-none absolute left-1/2 top-1/3 -z-10 h-[400px] w-[600px] -translate-x-1/2 rounded-full bg-accent/10 blur-[120px]" />
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm rounded-[2rem] border border-white/60 bg-white/70 p-8 shadow-soft backdrop-blur-xl"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-white shadow-glow">
            <Lock size={26} />
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold text-ink">Admin Access</h1>
          <p className="mt-1 text-sm text-ink-muted">Enter your password to view captured leads.</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="float-field">
            <input
              id="admin-password"
              data-testid="admin-password"
              type="password"
              placeholder=" "
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              className={error ? "has-error" : ""}
              autoFocus
            />
            <label htmlFor="admin-password">Password</label>
          </div>

          {error && <p data-testid="admin-error" className="pl-1 text-sm font-medium text-red-500">{error}</p>}

          <motion.button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            whileHover={!loading ? { scale: 1.02 } : {}}
            whileTap={!loading ? { scale: 0.98 } : {}}
            className="btn-pill w-full bg-accent px-6 py-3.5 text-base text-white disabled:opacity-70"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Verifying…</> : <>Unlock Dashboard</>}
          </motion.button>
        </form>

        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-ink-muted hover:text-accent">
          <ArrowLeft size={15} /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}

function LeadsDashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);
  const [exporting, setExporting] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/leads`, authHeader);
      setLeads(res.data.leads || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
        return;
      }
      setError("Could not load leads. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyPhone = (phone, id) => {
    navigator.clipboard?.writeText(phone);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/leads/export`, { ...authHeader, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.setAttribute("download", `s1ep-leads-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
    } finally {
      setExporting(false);
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="min-h-screen">
      <div className="container-x py-10">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/" className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-ink-muted hover:text-accent">
              <ArrowLeft size={16} /> Back to site
            </Link>
            <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-white"><Zap size={18} strokeWidth={2.6} /></span>
              Captured Leads
            </h1>
            <p className="mt-1 text-ink-muted">Every lead the engine intercepted, newest first.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-4 py-2 text-sm font-semibold text-accent-dark">
              <Users size={16} /> {leads.length} total
            </span>
            <button
              data-testid="admin-export"
              onClick={exportCsv}
              disabled={exporting || leads.length === 0}
              className="btn-pill bg-accent px-4 py-2 text-sm text-white shadow-glow hover:bg-accent-dark disabled:opacity-50"
            >
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              Export CSV
            </button>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="btn-pill border border-slate-200 bg-white px-4 py-2 text-sm text-ink-soft hover:border-accent hover:text-accent"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="btn-pill border border-slate-200 bg-white px-4 py-2 text-sm text-ink-soft hover:border-red-300 hover:text-red-500"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 w-full animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-red-600">{error}</div>
        ) : leads.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-200 bg-white/60 p-16 text-center">
            <p className="font-display text-xl font-semibold text-ink">No leads yet</p>
            <p className="mt-2 text-ink-muted">Submit the form on the landing page to see it appear here.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-card">
            <div className="hidden grid-cols-12 gap-4 border-b border-slate-100 bg-slate-50/70 px-6 py-4 text-xs font-semibold uppercase tracking-wider text-ink-muted md:grid">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Business</div>
              <div className="col-span-3">Phone</div>
              <div className="col-span-2">Captured</div>
              <div className="col-span-1">Status</div>
            </div>
            <AnimatePresence initial={false}>
              {leads.map((l) => (
                <motion.div
                  key={l.id}
                  data-testid="lead-row"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 gap-2 border-b border-slate-50 px-6 py-4 last:border-0 hover:bg-slate-50/50 md:grid-cols-12 md:items-center md:gap-4"
                >
                  <div className="col-span-3 font-semibold text-ink">{l.name}</div>
                  <div className="col-span-3 text-ink-soft">{l.business_name}</div>
                  <div className="col-span-3 flex items-center gap-2 text-ink-soft">
                    <span>{l.phone}</span>
                    <button onClick={() => copyPhone(l.phone, l.id)} className="text-ink-faint hover:text-accent" title="Copy phone">
                      {copied === l.id ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}
                    </button>
                  </div>
                  <div className="col-span-2 text-sm text-ink-muted">{fmt(l.created_at)}</div>
                  <div className="col-span-1">
                    <span className="inline-flex rounded-full bg-accent-soft px-2.5 py-1 text-xs font-semibold capitalize text-accent-dark">
                      {l.status}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
